import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from 'src/components/ui/dialog';
import { ScreenshotterItem } from '../types/screenshotter.types';
import { ChevronDown, Loader2, PencilLine, Save } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import AdPreviewStudio from './ad-preview-studio';

interface ScreenshotterTableProps {
  items: ScreenshotterItem[];
  isLoading?: boolean;
  onItemClick?: (item: ScreenshotterItem) => void;
  className?: string;
  showEmptyState?: boolean;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  } else {
    return num.toString();
  }
};

const ScreenshotterCards: React.FC<ScreenshotterTableProps> = ({
  items,
  isLoading = false,
  onItemClick,
  className = '',
  showEmptyState = true,
}) => {

  const [inputUrl, setInputUrl] = useState<string>('');
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [selectedScreenshotterItem, setSelectedScreenshotterItem] = useState<ScreenshotterItem | null>(null);
  const [showMapBox, setShowMapBox] = useState(false);
  const [inputMode, setInputMode] = useState(false);

  const defaultUrls = [
    'https://www.espn.com',
    'https://www.cbssports.com',
    'https://www.si.com',
    'https://www.bleacherreport.com',
    'https://www.bhg.com',
    'https://www.realsimple.com',
    'https://www.healthline.com',
    'https://www.allrecipes.com',
    'https://www.familyhandyman.com',
    'https://www.investopedia.com',
    'https://www.marketwatch.com',
    'https://www.fortune.com',
    'https://www.apnews.com',
    'https://www.reuters.com',
    'https://www.bbc.com',
    'https://www.techradar.com',
    'https://www.pcmag.com',
    'https://www.rollingstone.com',
    'https://www.usweekly.com',
    'https://www.yahoo.com',
  ];

  useEffect(() => {
    if (selectedScreenshotterItem) {
      setShowMapBox(true);
    } else {
      setShowMapBox(false);
      setSelectedUrl('');
      setInputUrl('');
    }
  }, [selectedScreenshotterItem]);

  useEffect(() => {
    if (!showMapBox) {
      setSelectedUrl('');
      setInputUrl('');
    }
  }, [showMapBox]);

  const formatUrl = (url: string): string => {
    return url.replace('https://', '').replace('http://', '').split('/')[0];
  };

  return (
    <>

      {/* Loading Mask */}
      {isLoading && (
        <div>
          <Loader2 className='w-4 h-4 animate-spin' />
        </div>
      )}
      <div className='w-full'>
        <div className='columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-4 gap-6 space-y-4'>
          {items.map((item) => (
            <div
              className='rounded-lg shadow overflow-hidden relative break-inside-avoid mb-4 w-full cursor-pointer'
              key={item.id}
              onClick={() => {
                setShowMapBox(true);
                setSelectedScreenshotterItem(item);
                onItemClick?.(item);
              }}
            >
              <div className='w-full bg-white'>
                <img
                  src={item.asset_url}
                  alt="Asset"
                  className="rounded-md w-full h-auto object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog
        open={showMapBox}
        onOpenChange={setShowMapBox}
      >
        <DialogContent
          className='bg-white w-7/8 max-w-full max-h-full m-6 p-0 focus:outline-none overflow-hidden'
          title="Geo Mapper Map View"
          tabIndex={-1}
        >
          <div className='w-[calc(100vw-10rem)] h-[calc(100vh-10rem)]'>
            <div className='w-full border-b border-gray-200 p-3 flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Button
                  onClick={() => {
                    setInputMode(!inputMode);
                  }}
                  variant="ghost"
                  size="med"
                  className='text-black hover:opacity-65 bg-gray-200'
                >
                  <PencilLine className="w-4 h-4" />
                </Button>
                {inputMode ? (
                  <div className='flex items-center gap-2'>
                    <Input
                      placeholder='Enter URL'
                      size={36}
                      className='w-3/4 bg-gray-200 text-black rounded-full'
                      value={inputUrl}
                      onChange={(e) => setInputUrl(e.target.value)}
                    />
                    <Button
                      variant="primary"
                      size="med"
                      className='text-white hover:opacity-65 bg-black'
                      onClick={() => {
                        setSelectedUrl(inputUrl);
                      }}
                    >
                      <Save className='w-4 h-4' />
                    </Button>
                  </div>

                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className={`ml-2 text-black hover:opacity-65 bg-gray-200`}
                        variant="ghost"
                        size="med"
                      >
                        <ChevronDown className="w-4 h-4" />
                        {selectedUrl || 'Select a URL'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {Object.values(defaultUrls).map((url) => (
                        <DropdownMenuItem
                          key={url}
                          onClick={() => setSelectedUrl(url)}
                        >
                          {formatUrl(url)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

              </div>

            </div>
            <div className='w-full h-full'>
              <AdPreviewStudio
                item={selectedScreenshotterItem || {}}
                targetUrl={selectedUrl}
              />
              {/* <ScreenshotterViewbox
                item={selectedScreenshotterItem || {}}
                targetUrl={selectedUrl}
                mode={selectedScreenshotterItem?.mode || 'all'}
              /> */}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScreenshotterCards;
